import React, { useState, useCallback, useEffect } from 'react';
import '@/App.css';
import { Download, ChevronDown, Bug, Sparkles, FileText } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { WorksheetPreview } from './components/WorksheetPreview';
import { generatePDF } from './lib/pdfGenerator';
import { TEMPLATES } from './lib/worksheetLayout';

// Hardcoded templates as fallback
const TEMPLATE_OPTIONS = [
  { id: 'template1', name: 'Hollydog Loves You', localPath: '/templates/template1.png' },
  { id: 'template2', name: 'Like the Way You Think', localPath: '/templates/template2.png' },
  { id: 'template3', name: "There's Only One You", localPath: '/templates/template3.png' }
];

// Use imported TEMPLATES or fallback
const templateList = TEMPLATES && TEMPLATES.length > 0 ? TEMPLATES : TEMPLATE_OPTIONS;

// Debounce hook for name input
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
};

function App() {
  // State
  const [name, setName] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(templateList[0].id);
  const [debugMode, setDebugMode] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [calculatedFontSize, setCalculatedFontSize] = useState(72);

  // Debounced name for preview updates
  const debouncedName = useDebounce(name, 150);

  // Get current template
  const currentTemplate = templateList.find(t => t.id === selectedTemplate) || templateList[0];

  // Handle name input
  const handleNameChange = (e) => {
    let value = e.target.value;
    // Allow letters, numbers, spaces, hyphens, and special characters
    setName(value);
  };

  // Handle name input blur (trim spaces)
  const handleNameBlur = () => {
    setName(name.trim());
  };

  // Handle PDF download
  const handleDownload = async () => {
    if (!debouncedName.trim()) {
      toast.error('Please enter a name first!', {
        description: 'Type the child\'s name in the input field above.',
        icon: <FileText className="w-5 h-5" />
      });
      return;
    }

    setIsGenerating(true);
    
    try {
      await generatePDF(debouncedName.trim(), currentTemplate.localPath, debouncedName.trim());
      toast.success('PDF Downloaded!', {
        description: `Worksheet for "${debouncedName.trim()}" is ready to print.`,
        icon: <Sparkles className="w-5 h-5" />
      });
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Oops! Something went wrong', {
        description: 'Please try again or refresh the page.'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle font size callback from preview
  const handleFontSizeCalculated = useCallback((fontSize) => {
    setCalculatedFontSize(fontSize);
  }, []);

  return (
    <div className="app-container pb-4">
      <Toaster 
        position="top-center" 
        richColors
        toastOptions={{
          style: {
            fontFamily: 'Nunito, sans-serif',
          }
        }}
      />
      
      {/* Header */}
      <header className="app-header pt-6 pb-4">
        <h1 className="app-title" data-testid="app-title">
          Hollydog Name Tracing Generator
        </h1>
        <p className="app-subtitle">
          Create fun name tracing worksheets for little learners!
        </p>
      </header>

      {/* Main content */}
      <main className="px-4 max-w-7xl mx-auto">
        <div className="lg:desktop-layout">
          {/* Controls Panel */}
          <div className="controls-sticky">
            <div className="controls-panel" data-testid="controls-panel">
              {/* Name Input, Template Select, and Download Button - All in One Row */}
              <div className="controls-row">
                <div className="control-item">
                  <label htmlFor="name-input" className="form-label">
                    Child's Name
                  </label>
                  <input
                    id="name-input"
                    type="text"
                    className="name-input"
                    placeholder="Enter name here..."
                    value={name}
                    onChange={handleNameChange}
                    onBlur={handleNameBlur}
                    maxLength={30}
                    data-testid="name-input"
                    autoComplete="off"
                    autoCapitalize="words"
                  />
                </div>

                <div className="control-item">
                  <label htmlFor="template-select" className="form-label">
                    Choose a Template
                  </label>
                  <div className="template-select-container">
                    <select
                      id="template-select"
                      className="template-select"
                      value={selectedTemplate}
                      onChange={(e) => setSelectedTemplate(e.target.value)}
                      data-testid="template-select"
                    >
                      <option value="template1">Hollydog Loves You</option>
                      <option value="template2">Like the Way You Think</option>
                      <option value="template3">There's Only One You</option>
                    </select>
                    <ChevronDown className="template-select-icon w-6 h-6" />
                  </div>
                </div>

                <div className="control-item control-item-btn">
                  <label className="form-label form-label-spacer">&nbsp;</label>
                  <button
                    className="download-btn"
                    onClick={handleDownload}
                    disabled={isGenerating}
                    data-testid="download-btn"
                  >
                    {isGenerating ? (
                      <>
                        <div className="loading-spinner w-5 h-5" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Download className="w-5 h-5" />
                        Download PDF
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Preview Section */}
          <section className="preview-section" data-testid="preview-section">
            <div className="preview-wrapper">
              <div className="worksheet-aspect-container">
                <WorksheetPreview
                  name={debouncedName.trim()}
                  templatePath={currentTemplate.localPath}
                  debugMode={debugMode}
                  onFontSizeCalculated={handleFontSizeCalculated}
                />
              </div>
            </div>
          </section>
        </div>
      </main>

    </div>
  );
}

export default App;
